import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import inflection from "inflection";

import { makeStyles, withStyles } from "@material-ui/core/styles";

import MuiTable from "@material-ui/core/Table";

import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MuiCheckbox from "@material-ui/core/Checkbox";
import MuiTextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import MuiToolbar from "@material-ui/core/Toolbar";
import Divider from "@material-ui/core/Divider";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Switch from "@material-ui/core/Switch";
import Badge from "@material-ui/core/Badge";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import { ReactSortable } from "react-sortablejs";

import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Create";
import DeleteIcon from "@material-ui/icons/Delete";
import DownloadIcon from "@material-ui/icons/GetApp";
import SubmitIcon from "@material-ui/icons/Send";
import FalseIcon from "@material-ui/icons/Clear";
import TrueIcon from "@material-ui/icons/Done";

import JReferenceField from "jazasoft/lib/mui/field/ReferenceField";
import TextField from "jazasoft/lib/mui/field/TextField";
import translate from "jazasoft/lib/i18n/translate";
import { isEmpty } from "../utils/helpers";

export const CheckBox = React.memo(({ rowIdx, colIdx, column, record, disabled, onChange, ...restProps }) => (
  <MuiCheckbox
    style={{ paddingTop: 0, paddingBottom: 0 }}
    checked={record[column.dataKey] || false}
    onChange={onChange && onChange({ rowIdx, colIdx, column, record })}
    disabled={typeof disabled === "function" ? disabled({ rowIdx, colIdx, column, record }) : disabled}
    {...restProps}
  />
));

export const NativeTextInput = React.memo(({ rowIdx, colIdx, column, record, onChange, onPaste, type, disabled, style = {}, ...restProps }) => (
  <input
    style={{
      width: "100%",
      minWidth: 75,
      padding: 8,
      border: "none",
      ...style
    }}
    type={type}
    value={record[column.dataKey] === null || record[column.dataKey] === undefined ? "" : record[column.dataKey]}
    onChange={onChange && onChange({ rowIdx, colIdx, column, record })}
    onPaste={onPaste && onPaste({ rowIdx, colIdx, column, record })}
    disabled={typeof disabled === "function" ? disabled({ rowIdx, colIdx, column, record }) : disabled}
    {...restProps}
  />
));

export const TextInput = React.memo(({ rowIdx, colIdx, column, record, onChange, onPaste, type, width = 120, disabled, ...restProps }) => (
  <MuiTextField
    style={{ width }}
    type={type}
    value={record[column.dataKey] === null || record[column.dataKey] === undefined ? "" : record[column.dataKey]}
    onChange={onChange && onChange({ rowIdx, colIdx, column, record })}
    onPaste={onPaste && onPaste({ rowIdx, colIdx, column, record })}
    disabled={typeof disabled === "function" ? disabled({ rowIdx, colIdx, column, record }) : disabled}
    {...restProps}
  />
));

export const BooleanInput = React.memo(({ rowIdx, colIdx, column, record, onChange, disabled }) => (
  <FormGroup>
    <FormControlLabel
      control={
        <Switch
          color="primary"
          checked={record[column.dataKey] || false}
          onChange={onChange && onChange({ rowIdx, colIdx, column, record })}
          disabled={typeof disabled === "function" ? disabled({ rowIdx, colIdx, column, record }) : disabled}
        />
      }
    />
  </FormGroup>
));

export const SelectInput = React.memo(({ rowIdx, colIdx, column, record, onChange, choices, allowEmpty, width = 120, disabled, ...restProps }) => {
  const options = typeof choices === "function" ? choices({ rowIdx, colIdx, column, record }) : choices;
  return (
    <Select
      style={{ width }}
      value={record[column.dataKey] || (allowEmpty || !options[0] ? "" : options[0].id)}
      onChange={onChange && onChange({ rowIdx, colIdx, column, record })}
      disabled={typeof disabled === "function" ? disabled({ rowIdx, colIdx, column, record }) : disabled}
      {...restProps}>
      {allowEmpty && (
        <MenuItem key={"null"} value={null}>
          &nbsp;
        </MenuItem>
      )}
      {options.map(({ id, name }) => (
        <MenuItem key={id} value={id}>
          {name}
        </MenuItem>
      ))}
    </Select>
  );
});

export const ReferenceField = React.memo(({ column, record, refrenceSource, reference, allowEmpty, linkType = false }) => (
  <JReferenceField record={record} source={column.dataKey} reference={reference} allowEmpty={allowEmpty} linkType={linkType}>
    <TextField source={refrenceSource} />
  </JReferenceField>
));

export const BooleanField = React.memo(({ rowIdx, colIdx, column, record, render }) => (
  <Typography component="span" body1="body1">
    {typeof render === "function" ? render({ rowIdx, colIdx, column, record }) : record[column.dataKey] === true ? <TrueIcon /> : <FalseIcon />}
  </Typography>
));

export const LinkField = React.memo(({ rowIdx, colIdx, column, record, onClick }) => (
  <div style={{ color: "#3F51B5", cursor: "pointer" }} onClick={onClick && onClick({ rowIdx, colIdx, column, record })}>
    {record[column.dataKey]}
  </div>
));

export const DefaultRowRenderer = React.memo(({ classes = {}, rowStyle, cellStyle, columns, record, rowIdx }) => {
  return (
    <TableRow key={rowIdx} style={rowStyle}>
      {columns.map((column, colIdx) => {
        const { dataKey, align = "left", element, rowSpan } = column;
        let rSpan = 1;
        if (rowSpan && typeof rowSpan === "function") {
          rSpan = rowSpan({
            rowIdx,
            colIdx,
            record,
            column: columns[colIdx]
          });
        }
        let cellItem = null;
        if (element) {
          if (typeof element === "function") {
            cellItem = element({
              rowIdx,
              colIdx,
              column,
              record
            });
          } else {
            cellItem =
              React.isValidElement(element) &&
              React.cloneElement(element, {
                rowIdx,
                colIdx,
                column,
                record
              });
          }
        } else {
          cellItem = record[dataKey] === null ? "-" : record[dataKey];
        }
        return rSpan > 0 ? (
          <TableCell className={classes.rowCell} key={colIdx} rowSpan={rSpan} align={align} style={cellStyle ? cellStyle({ record, column, rowIdx, colIdx }) : {}}>
            {cellItem}
          </TableCell>
        ) : null;
      })}
    </TableRow>
  );
});

const useToolbarStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  // highlight:
  //   theme.palette.type === "light"
  //     ? {
  //         color: theme.palette.secondary.main,
  //         backgroundColor: lighten(theme.palette.secondary.light, 0.85)
  //       }
  //     : {
  //         color: theme.palette.text.primary,
  //         backgroundColor: theme.palette.secondary.dark
  //       },
  spacer: {
    flex: "1 1 100%"
  },
  actions: {
    color: theme.palette.text.secondary
  },
  title: {
    flex: "0 0 auto"
  }
}));

export const Toolbar = React.memo(({ title, badgeContent, actions = [] }) => {
  const classes = useToolbarStyles();
  return (
    <MuiToolbar className={classes.root}>
      <div className={classes.title}>
        {badgeContent !== undefined && (
          <Badge color="primary" badgeContent={badgeContent} className={classes.margin}>
            <Typography variant="subtitle1" color="primary">
              {title}
            </Typography>
          </Badge>
        )}
        {(badgeContent === undefined || badgeContent === null) && (
          <Typography variant="subtitle1" color="primary">
            {title}
          </Typography>
        )}
      </div>
      <div className={classes.spacer} />
      <div className={classes.actions}>
        {actions.map(({ name, label, tooltip, ...rest }, idx) => {
          if (["add", "edit", "delete", "download", "submit"].includes(name)) {
            return (
              <Tooltip key={idx} title={tooltip || inflection.humanize(name)}>
                <IconButton aria-label={tooltip || inflection.humanize(name)} {...rest}>
                  {name === "add" && <AddIcon />}
                  {name === "edit" && <EditIcon />}
                  {name === "delete" && <DeleteIcon />}
                  {name === "download" && <DownloadIcon />}
                  {name === "submit" && <SubmitIcon />}
                  {label}
                </IconButton>
              </Tooltip>
            );
          } else if (name === "search") {
            return (
              <MuiTextField
                key={idx}
                className={rest.className}
                value={rest.value}
                onChange={e => rest.onSearch && rest.onSearch(e.target.value)}
                placeholder={rest.placeholder}
                color="inherit"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title={tooltip || "Search"}>
                        <SearchIcon color="inherit" fontSize="small" />
                      </Tooltip>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled={!rest.value} onClick={() => rest.onSearch && rest.onSearch("")}>
                        <ClearIcon color="inherit" fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                style={{ width: 224 }}
              />
            );
          } else {
            return null;
          }
        })}
      </div>
    </MuiToolbar>
  );
});

Toolbar.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  badgeContent: PropTypes.number,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.oneOf(["add", "search", "submit"]),
      label: PropTypes.string,
      onClick: PropTypes.func,
      onSearch: PropTypes.func,
      tooltip: PropTypes.string,
      placeholder: PropTypes.string
    })
  )
};

const styles = {
  tableContainer: {
    overFlowX: "auto"
  },
  table: {},
  headerCell: {
    padding: "8px",
    fontSize: 12,
    color: "#3C4858",
    "&:last-child": {
      padding: "8px",
      fontSize: 12,
      color: "#3C4858"
    }
  },
  rowCell: {
    padding: "8px",
    fontSize: 12,
    "&:last-child": {
      padding: "8px",
      fontSize: 12
    }
  },
  fixedHeader: {
    position: "sticky",
    top: 0,
    backgroundColor: "white"
  }
};

const Table = React.memo(({ classes, ...props }) => {
  const {
    id,
    columns,
    headerRows,
    rows,
    setRows,
    handle,
    rowRenderer,
    rowStyle,
    cellStyle,
    headerCellStyle,
    rowSelectAll,
    onSelectAll,
    fixedHeader,
    toolbar,
    translate,
    i18nKey
  } = props;

  return (
    <div>
      {toolbar && <div>{typeof toolbar === "function" ? toolbar() : React.isValidElement(toolbar) ? React.cloneElement(toolbar) : toolbar}</div>}
      {toolbar && <Divider />}
      <div className={classes.tableContainer}>
        {rows && rows.length > 0 && (
          <MuiTable className={classes.table} id={id}>
            <TableHead>
              {isEmpty(headerRows) && (
                <TableRow>
                  {columns.map(({ dataKey, title, align = "left", selectAll, ...rest }, idx) => {
                    if (i18nKey) {
                      title = translate(`${i18nKey}.${dataKey}`, {
                        _: title
                      });
                    }
                    return (
                      <TableCell
                        key={idx}
                        align={align}
                        className={classnames({
                          [classes.fixedHeader]: fixedHeader,
                          [classes.headerCell]: true
                        })}
                        style={
                          headerCellStyle
                            ? headerCellStyle({
                                dataKey,
                                title,
                                align,
                                selectAll,
                                ...rest
                              })
                            : {}
                        }>
                        {selectAll && (
                          <MuiCheckbox
                            style={{ paddingTop: 0, paddingBottom: 0 }}
                            checked={rowSelectAll[dataKey] || false}
                            onChange={e => onSelectAll && onSelectAll(dataKey, e.target.checked)}
                            indeterminate={rowSelectAll[dataKey] === null}
                          />
                        )}
                        {title}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}
              {!isEmpty(headerRows) &&
                headerRows.map((headerRow, rowIdx) => {
                  const cols = columns.flatMap((c, colIdx) => {
                    let colSpan = 1;
                    if (typeof c.colSpan === "function") {
                      colSpan = c.colSpan({ column: c, row: headerRow, colIdx, rowIdx });
                    }
                    return colSpan === 0 ? [] : { ...c, colSpan };
                  });
                  return (
                    <TableRow key={rowIdx}>
                      {cols.map(({ dataKey, align = "left", colSpan = 1, ...rest }, idx) => {
                        const value = headerRow[dataKey];
                        return (
                          <TableCell
                            key={`${rowIdx}-${idx}`}
                            colSpan={colSpan}
                            align={align}
                            className={classnames({
                              [classes.fixedHeader]: fixedHeader,
                              [classes.headerCell]: true
                            })}
                            style={
                              headerCellStyle
                                ? headerCellStyle({
                                    dataKey,
                                    align,
                                    ...rest
                                  })
                                : {}
                            }>
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableHead>
            <ReactSortable tag="tbody" list={rows} setList={setRows} handle={handle}>
              {rows.map((record, rowIdx) =>
                typeof rowRenderer === "function"
                  ? rowRenderer({
                      rowStyle: rowStyle(record),
                      cellStyle,
                      classes,
                      columns,
                      record,
                      rowIdx
                    })
                  : React.cloneElement(rowRenderer, {
                      rowStyle: rowStyle(record),
                      cellStyle,
                      classes,
                      columns,
                      record,
                      rowIdx,
                      key: rowIdx
                    })
              )}
            </ReactSortable>
          </MuiTable>
        )}
        {(!rows || rows.length === 0) && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "1.5em"
            }}>
            <Typography>{props.emptyMessage}</Typography>
          </div>
        )}
      </div>
    </div>
  );
});

Table.propTypes = {
  fixedHeader: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string,
      title: PropTypes.string,
      align: PropTypes.oneOf(["left", "right", "center"]),
      element: PropTypes.any,
      rowSpan: PropTypes.func, // ({rowIdx, colIdx, record, column}) => Number (rowSpan: 0 or more)
      selectAll: PropTypes.bool
    })
  ),
  headerRows: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  setRows: PropTypes.func,
  handle: PropTypes.string,
  rowRenderer: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  rowStyle: PropTypes.func,
  cellStyle: PropTypes.func,
  headerCellStyle: PropTypes.func,
  rowSelectAll: PropTypes.object,
  onSelectAll: PropTypes.func,
  toolbar: PropTypes.any,
  emptyMessage: PropTypes.string,
  i18nKey: PropTypes.string
};

Table.defaultProps = {
  fixedHeader: true,
  id: "table",
  rowRenderer: <DefaultRowRenderer />,
  rowStyle: record => {},
  cellStyle: ({ record, column }) => {},
  headerCellStyle: column => {},
  emptyMessage: "No record available",
  setRows: () => {},
  handle: ".handle"
};

export default translate(withStyles(styles)(Table));
